import React, { useState, useEffect, useRef, useCallback } from 'react';
import { entityAPI } from '../lib/api';
import { getRiskColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ForceGraph2D from 'react-force-graph-2d';
import {
  Search,
  ZoomIn,
  ZoomOut,
  Maximize,
  RefreshCw,
  Network,
  Users,
  Building,
  Wallet,
  CreditCard,
  AlertTriangle,
  Database,
  ChevronRight,
  Link as LinkIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ENTITY_ICONS = {
  person: Users,
  company: Building,
  wallet: Wallet,
  account: CreditCard,
};

export default function NetworkAnalysis() {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(false);
  const [searchEntity, setSearchEntity] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [hoverNode, setHoverNode] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [graphStats, setGraphStats] = useState({ nodes: 0, links: 0, avgConnections: 0 });
  const graphRef = useRef();
  const containerRef = useRef();

  // Fetch entities on mount
  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const response = await entityAPI.list({ limit: 100 });
      setEntities(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    }
  };

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/seed-demo-data');
      toast.success(response.data.message);
      await fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleEntitySelect = async (entityId) => {
    if (!entityId || entityId === 'none') {
      setSelectedEntityId('');
      return;
    }
    setSelectedEntityId(entityId);
    setLoading(true);
    
    try {
      const networkRes = await entityAPI.getNetwork(entityId, 2);
      
      // Transform for force graph
      const nodes = networkRes.data.nodes.map((n) => ({
        id: n.id,
        name: n.name,
        type: n.type,
        risk_level: n.risk_level,
        color: getRiskColor(n.risk_level),
        val: n.id === entityId ? 25 : 15,
        isCenter: n.id === entityId,
      }));

      const links = networkRes.data.edges.map((e) => ({
        source: e.source,
        target: e.target,
        type: e.type,
        weight: e.weight,
        color: `rgba(0, 240, 255, ${0.2 + (e.weight || 1) * 0.1})`,
      }));

      setGraphData({ nodes, links });
      
      // Calculate stats
      const avgConn = nodes.length > 0 ? (links.length * 2 / nodes.length).toFixed(1) : 0;
      setGraphStats({ nodes: nodes.length, links: links.length, avgConnections: avgConn });
      
      const entity = entities.find(e => e.id === entityId);
      toast.success(`Loaded network for ${entity?.name || 'entity'}`);
      
      // Auto-fit view
      setTimeout(() => {
        if (graphRef.current) {
          graphRef.current.zoomToFit(500, 50);
        }
      }, 100);
    } catch (error) {
      toast.error('Failed to load network');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchEntity.trim()) return;

    setLoading(true);
    try {
      const searchRes = await entityAPI.list({ search: searchEntity, limit: 1 });
      if (searchRes.data.items.length === 0) {
        toast.error('Entity not found');
        setLoading(false);
        return;
      }

      const entity = searchRes.data.items[0];
      setSelectedEntityId(entity.id);
      await handleEntitySelect(entity.id);
    } catch (error) {
      toast.error('Failed to search entity');
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    
    // Highlight connected nodes
    const connectedNodes = new Set();
    const connectedLinks = new Set();
    
    graphData.links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      
      if (sourceId === node.id) {
        connectedNodes.add(targetId);
        connectedLinks.add(link);
      }
      if (targetId === node.id) {
        connectedNodes.add(sourceId);
        connectedLinks.add(link);
      }
    });
    
    connectedNodes.add(node.id);
    setHighlightNodes(connectedNodes);
    setHighlightLinks(connectedLinks);
    
    if (graphRef.current) {
      graphRef.current.centerAt(node.x, node.y, 1000);
      graphRef.current.zoom(2.5, 1000);
    }
  }, [graphData.links]);

  const handleNodeHover = useCallback((node) => {
    setHoverNode(node);
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null);
    setHighlightNodes(new Set());
    setHighlightLinks(new Set());
  }, []);

  const handleZoomIn = () => {
    if (graphRef.current) {
      graphRef.current.zoom(graphRef.current.zoom() * 1.5, 500);
    }
  };

  const handleZoomOut = () => {
    if (graphRef.current) {
      graphRef.current.zoom(graphRef.current.zoom() / 1.5, 500);
    }
  };

  const handleFitView = () => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(500, 50);
    }
  };

  const getConnectedCount = (nodeId) => {
    return graphData.links.filter(l => {
      const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
      const targetId = typeof l.target === 'object' ? l.target.id : l.target;
      return sourceId === nodeId || targetId === nodeId;
    }).length;
  };

  const getNodePaint = useCallback((node, ctx, globalScale) => {
    const isHighlighted = highlightNodes.size === 0 || highlightNodes.has(node.id);
    const opacity = isHighlighted ? 1 : 0.3;
    const nodeSize = node.val / 2;
    
    // Glow effect for center node
    if (node.isCenter) {
      ctx.shadowColor = node.color;
      ctx.shadowBlur = 20;
    }
    
    // Node circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI);
    ctx.fillStyle = node.color;
    ctx.globalAlpha = opacity;
    ctx.fill();
    
    // Border
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = node.isCenter ? 2 : 1;
    ctx.stroke();
    
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
    
    // Label
    const label = node.name.length > 15 ? node.name.substring(0, 12) + '...' : node.name;
    const fontSize = Math.max(10 / globalScale, 3);
    ctx.font = `${fontSize}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = isHighlighted ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)';
    ctx.fillText(label, node.x, node.y + nodeSize + 2);
  }, [highlightNodes]);

  const getLinkPaint = useCallback((link, ctx) => {
    const isHighlighted = highlightLinks.size === 0 || highlightLinks.has(link);
    const opacity = isHighlighted ? 0.6 : 0.15;
    
    ctx.strokeStyle = `rgba(0, 240, 255, ${opacity})`;
    ctx.lineWidth = (link.weight || 1) * (isHighlighted ? 1.5 : 1);
    
    ctx.beginPath();
    const source = link.source;
    const target = link.target;
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
  }, [highlightLinks]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="network-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Network Analysis
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Relationship mapping and link analysis
          </p>
        </div>
        
        {entities.length === 0 && (
          <Button 
            onClick={seedDemoData} 
            disabled={seeding}
            className="gap-2"
            data-testid="seed-demo-btn"
          >
            {seeding ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Database className="w-4 h-4" />
            )}
            Load Demo Data
          </Button>
        )}
      </div>

      {/* Search & Entity Selection */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Entity Dropdown */}
            <div className="flex-1">
              <Select 
                value={selectedEntityId || "none"} 
                onValueChange={handleEntitySelect}
              >
                <SelectTrigger 
                  className="bg-black/20 border-white/10"
                  data-testid="entity-select"
                >
                  <SelectValue placeholder="Select an entity to analyze..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-[300px]">
                  <SelectItem value="none">Select an entity...</SelectItem>
                  {entities.map((entity) => {
                    const IconComponent = ENTITY_ICONS[entity.entity_type] || Users;
                    return (
                      <SelectItem key={entity.id} value={entity.id}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          <span>{entity.name}</span>
                          <Badge 
                            variant="outline" 
                            className={`ml-2 text-[9px] ${
                              entity.risk_level === 'critical' ? 'border-red-500 text-red-500' :
                              entity.risk_level === 'high' ? 'border-orange-500 text-orange-500' :
                              entity.risk_level === 'medium' ? 'border-yellow-500 text-yellow-500' :
                              'border-green-500 text-green-500'
                            }`}
                          >
                            {entity.risk_level}
                          </Badge>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex gap-2 flex-1">
              <Input
                value={searchEntity}
                onChange={(e) => setSearchEntity(e.target.value)}
                placeholder="Or search by name..."
                className="bg-black/20 border-white/10"
                data-testid="network-search-input"
              />
              <Button type="submit" disabled={loading} data-testid="analyze-network-btn">
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Stats Bar */}
      {graphData.nodes.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="glass">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{graphStats.nodes}</p>
                <p className="text-xs text-muted-foreground">Entities</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <LinkIcon className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{graphStats.links}</p>
                <p className="text-xs text-muted-foreground">Connections</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Network className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{graphStats.avgConnections}</p>
                <p className="text-xs text-muted-foreground">Avg Connections</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Graph */}
        <Card className="glass lg:col-span-3">
          <CardHeader className="border-b border-white/5 pb-4 flex flex-row items-center justify-between">
            <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
              Network Graph
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" onClick={handleZoomIn} data-testid="zoom-in-btn">
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleZoomOut} data-testid="zoom-out-btn">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleFitView} data-testid="fit-view-btn">
                <Maximize className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div ref={containerRef} className="h-[600px] graph-container relative bg-black/20">
              {loading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <RefreshCw className="w-12 h-12 text-primary animate-spin mb-4" />
                  <p className="text-muted-foreground">Loading network data...</p>
                </div>
              ) : graphData.nodes.length > 0 ? (
                <ForceGraph2D
                  ref={graphRef}
                  graphData={graphData}
                  nodeLabel={(node) => `${node.name} (${node.type}) - Risk: ${node.risk_level}`}
                  nodeCanvasObject={getNodePaint}
                  linkCanvasObject={getLinkPaint}
                  onNodeClick={handleNodeClick}
                  onNodeHover={handleNodeHover}
                  onBackgroundClick={handleBackgroundClick}
                  backgroundColor="transparent"
                  width={containerRef.current?.clientWidth || 800}
                  height={600}
                  d3AlphaDecay={0.02}
                  d3VelocityDecay={0.3}
                  linkDirectionalParticles={2}
                  linkDirectionalParticleWidth={2}
                  linkDirectionalParticleSpeed={0.005}
                  cooldownTicks={100}
                  onEngineStop={() => graphRef.current?.zoomToFit(400, 50)}
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Network className="w-16 h-16 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No network data</p>
                  <p className="text-sm text-muted-foreground/50 mt-1">
                    {entities.length === 0 
                      ? 'Load demo data to start exploring networks'
                      : 'Select an entity to visualize its network'
                    }
                  </p>
                </div>
              )}
              
              {/* Hover tooltip */}
              {hoverNode && (
                <div className="absolute top-4 left-4 glass p-3 rounded-lg border border-white/10 pointer-events-none">
                  <p className="text-sm font-medium text-white">{hoverNode.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{hoverNode.type}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Details Panel */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
              {selectedNode ? 'Node Details' : 'Analysis Panel'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {selectedNode ? (
              <div className="space-y-4">
                <div>
                  <p className="label-mono">Name</p>
                  <p className="text-white font-medium">{selectedNode.name}</p>
                </div>
                <div>
                  <p className="label-mono">Type</p>
                  <div className="flex items-center gap-2 mt-1">
                    {(() => {
                      const IconComponent = ENTITY_ICONS[selectedNode.type] || Users;
                      return <IconComponent className="w-4 h-4 text-muted-foreground" />;
                    })()}
                    <p className="text-white capitalize">{selectedNode.type}</p>
                  </div>
                </div>
                <div>
                  <p className="label-mono">Risk Level</p>
                  <Badge
                    variant="outline"
                    className={`mt-1 ${
                      selectedNode.risk_level === 'critical' ? 'border-red-500 text-red-500 bg-red-500/10' :
                      selectedNode.risk_level === 'high' ? 'border-orange-500 text-orange-500 bg-orange-500/10' :
                      selectedNode.risk_level === 'medium' ? 'border-yellow-500 text-yellow-500 bg-yellow-500/10' :
                      'border-green-500 text-green-500 bg-green-500/10'
                    }`}
                  >
                    {selectedNode.risk_level}
                  </Badge>
                </div>
                <div className="pt-4 border-t border-white/10">
                  <p className="label-mono mb-2">Connected Nodes</p>
                  <p className="text-2xl font-secondary font-bold text-white">
                    {getConnectedCount(selectedNode.id)}
                  </p>
                </div>
                
                {/* Connected Entities List */}
                <div className="pt-4 border-t border-white/10">
                  <p className="label-mono mb-3">Direct Connections</p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {graphData.links
                      .filter(l => {
                        const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
                        const targetId = typeof l.target === 'object' ? l.target.id : l.target;
                        return sourceId === selectedNode.id || targetId === selectedNode.id;
                      })
                      .map((link, idx) => {
                        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                        const connectedId = sourceId === selectedNode.id 
                          ? (typeof link.target === 'object' ? link.target.id : link.target)
                          : sourceId;
                        const connectedNode = graphData.nodes.find(n => n.id === connectedId);
                        if (!connectedNode) return null;
                        
                        return (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-2 rounded bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                            onClick={() => handleNodeClick(connectedNode)}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div 
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: connectedNode.color }}
                              />
                              <span className="text-sm text-white truncate">{connectedNode.name}</span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => {
                    setSelectedNode(null);
                    setHighlightNodes(new Set());
                    setHighlightLinks(new Set());
                    handleFitView();
                  }}
                >
                  Clear Selection
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">
                  Click a node to see details
                </p>
                <p className="text-muted-foreground/50 text-xs mt-2">
                  Connected entities will be highlighted
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-6">
            <span className="label-mono">Risk Levels:</span>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FF003D' }} />
              <span className="text-sm text-muted-foreground">Critical</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FF6B00' }} />
              <span className="text-sm text-muted-foreground">High</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FF9900' }} />
              <span className="text-sm text-muted-foreground">Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#00FF94' }} />
              <span className="text-sm text-muted-foreground">Low</span>
            </div>
            
            <div className="border-l border-white/10 pl-6 flex items-center gap-6">
              <span className="label-mono">Entity Types:</span>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Person</span>
              </div>
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Company</span>
              </div>
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Wallet</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
